import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Block,
  BlockTitle,
  Button,
  Card,
  Chip,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Notification,
  Preloader,
} from 'konsta/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TabBar from '../components/TabBar';
import { useAppContext } from '../context/AppContext';
import { FaArrowLeftLong, FaCheck } from 'react-icons/fa6';
import { formatUSD } from '../utils';
import { PiHandWithdrawBold } from 'react-icons/pi';
import { popup, init, isTMA } from '@telegram-apps/sdk';
import { Post } from '../helpers/AxiosHelper';
import { MdError } from 'react-icons/md';
import { WithdrawResult } from '../models/Models';
import { useRefetchQuery } from '../context/useRefetchQuery';
import Lottie from 'lottie-react';
import lottieDollarSign from '../lotties/Crypto_Money in Hand 2-LwUaY.json';
import lottieSuccess from '../lotties/success.json';

const minimumWithdraw = Number(
  process.env.REACT_APP_MINIMUM_WITHDRAW_AMT_USD ?? 5,
);

const WithdrawPage = () => {
  const refetchQuery = useRefetchQuery();
  const { userProducts, balance } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState<string>('usdt_trx');
  const [walletAddress, setWalletAddress] = useState<string>();
  const [notificationFull, setNotificationFull] = useState(false);
  const [requestedSubmitted, setRequestSubmitted] = useState(false);
  const [withdrawRequestResult, setWithdrawRequestResult] =
    useState<WithdrawResult>();
  const [error, setError] = useState();
  const navigate = useNavigate();

  useEffect(() => {}, [userProducts, balance]);

  async function createWithdraw() {
    if (
      !currency ||
      !walletAddress ||
      walletAddress.trim().length === 0 ||
      loading
    ) {
      return;
    }

    let canContinue = false;

    if (popup.isSupported()) {
      const promise = await popup.open({
        title: 'Withdraw',
        message: 'Are you sure you want to proceed with the withdrawal?',
        buttons: [
          {
            text: 'Yes',
            type: 'default',
            id: 'yes',
          },
          {
            type: 'cancel',
            id: 'cancel',
          },
        ],
      });

      await promise;

      const buttonId = await promise;

      canContinue = buttonId === 'yes';
    }

    if (canContinue) {
      setLoading(true);

      const withdrawResult = await Post<
        any,
        { currency: string; walletAddress: string }
      >('/api/createWithdraw', {
        currency: currency,
        walletAddress: walletAddress,
      });

      if (withdrawResult.error && withdrawResult.error.trim().length > 0) {
        setError(withdrawResult.error);
        setNotificationFull(true);
        setTimeout(() => {
          setNotificationFull(false);
        }, 5000);
        setLoading(false);
        return;
      }

      setRequestSubmitted(true);
      setWithdrawRequestResult(withdrawResult);

      refetchQuery(['user_products']);
      refetchQuery(['products']);
      refetchQuery(['referral_stats']);
    }
  }

  return (
    <Page className=" pb-40">
      <Navbar
        title="Withdraw"
        className="top-0 sticky translucent"
        left={
          <FaArrowLeftLong
            className="mx-3 w-5 h-5"
            onClick={() => navigate(-1)}
          />
        }
      />

      <Notification
        opened={notificationFull}
        icon={<MdError className="text-red-500" />}
        title="Error"
        subtitle="Could not submit withdraw request."
        text={error}
      />

      {requestedSubmitted && withdrawRequestResult && (
        <>
          <Block>
            <div className="flex flex-col justify-center items-center">
              <div className="flex justify-center  overflow-hidden">
                <Lottie
                  animationData={lottieSuccess}
                  renderer="svg"
                  className="w-64 h-64"
                  style={{ overflow: 'hidden' }}
                />
              </div>
              <h1 className="text-3xl font-bold mb-2">Request submitted!</h1>
              <h3 className="text-center">
                We have received your withdraw request. Withdrawals are
                typically processed within 24 hours. Network congestion or
                additional verification may cause slight delays.
              </h3>
            </div>
          </Block>

          <List strong>
            <ListItem
              title="Request #"
              subtitle={
                <span className="text-primary">
                  {withdrawRequestResult?.orderNumber}
                </span>
              }
            />
            <ListItem
              title={`Wallet Address (${withdrawRequestResult?.currency.toUpperCase()})`}
              subtitle={
                <span className="text-primary">
                  {withdrawRequestResult?.walletAddress}
                </span>
              }
            />

            <ListItem
              title="Amount (USD)"
              after={
                <span className="text-primary currency tracking-widest">
                  {formatUSD(withdrawRequestResult.withdrawAmount!)}
                </span>
              }
            />
          </List>
        </>
      )}

      {!requestedSubmitted && (
        <>
          <div className="flex justify-center">
            <Lottie
              animationData={lottieDollarSign}
              renderer="svg"
              className="w-48 h-48"
            />
          </div>

          <List strongIos insetIos>
            <ListInput
              outline
              label="Withdraw Amount (USD)"
              disabled
              floatingLabel
              type="text"
              placeholder="0"
              value={formatUSD(balance, 6)}
            />

            <ListInput
              outline
              dropdown
              type="select"
              defaultValue={currency}
              label="Currency"
            >
              <option selected value={currency}>
                USDT (TRX)
              </option>
            </ListInput>

            <ListInput
              outline
              label="Wallet Address"
              floatingLabel
              value={walletAddress}
              onChange={(e) =>
                setWalletAddress(e.currentTarget.value.replace(' ', ''))
              }
            />
          </List>

          <Block className="text-sm">
            * <b>Important:</b> Please ensure that your wallet address is
            correct before submitting your withdrawal request. Transactions
            cannot be reversed once processed. <br />
            <br />* <b>Note:</b> Once the withdrawal request is submitted, all
            miner balances and referral earnings will be transferred to a
            temporary account for processing. As a result, your miner and
            referral balances will be set to zero until the payout is completed.
          </Block>

          <Block>
            {balance < minimumWithdraw && (
              <div className="text-red-500">
                Minimum withdraw amount is {formatUSD(minimumWithdraw)}
              </div>
            )}
            <Button
              disabled={
                loading ||
                !walletAddress ||
                walletAddress.trim().length < 16 ||
                balance <= 0 ||
                balance < minimumWithdraw
              }
              onClick={() => createWithdraw()}
            >
              {!loading && (
                <>
                  Submit Withdraw{' '}
                  <PiHandWithdrawBold className="ms-2 w-6 h-6" />
                </>
              )}
              {loading && (
                <div className="text-center">
                  <Preloader
                    size="w-4 h-4"
                    colors={{ iconMaterial: 'text-black' }}
                  />
                </div>
              )}
            </Button>
          </Block>
        </>
      )}

      <TabBar selectedTab="home" />
    </Page>
  );
};

export default WithdrawPage;
